import { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import CrossAssetDashboard from '../components/macro-radar/CrossAssetDashboard.jsx';
import NewsFeed from '../components/macro-radar/NewsFeed.jsx';
import RegimeIndicator from '../components/macro-radar/RegimeIndicator.jsx';
import CostDashboard from '../components/macro-radar/CostDashboard.jsx';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

const IMPORTANCE_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b' };

export default function MacroRadar() {
  const { setPage } = usePage();
  useEffect(() => { setPage('macro-radar'); }, []);

  const [telegramMsgs, setTelegramMsgs] = useState([]);
  const [summary, setSummary] = useState({ regime: null, activeRecs: 0, shiftProb: null });
  const { impact, track, loading } = usePageTracking('regime');

  useEffect(() => {
    async function loadTelegram() {
      try {
        const r = await fetch('/api/telegram/messages/recent?limit=5&importance=high');
        if (r.ok) setTelegramMsgs(await r.json());
      } catch {}
    }
    async function loadSummary() {
      try {
        const [regime, recs] = await Promise.all([
          fetch('/api/regime').then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('/api/kairos/recommendations?status=active').then(r => r.ok ? r.json() : null).catch(() => null),
        ]);
        setSummary({
          regime: regime || null,
          activeRecs: recs?.recommendations?.length || 0,
          shiftProb: regime?.shift_probability ?? null,
        });
      } catch {}
    }
    loadTelegram();
    loadSummary();
    const iv = setInterval(loadTelegram, 60000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-white">Macro Radar</h1>
        <span className="text-korinth-muted text-sm">Real-time geopolitical & macroeconomic intelligence</span>
      </div>

      {/* Sprint 20: Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Regime:</span> <span className="text-white font-bold">{summary.regime?.regime || 'Unknown'}</span> <span className="text-korinth-muted">({summary.regime?.confidence ?? 0}% conf)</span></span>
        {summary.shiftProb != null && <span><span className="text-korinth-muted">Shift probability:</span> <span className="text-korinth-warning font-bold">{summary.shiftProb}%</span></span>}
        <span><span className="text-korinth-muted">Regime-aligned active recs:</span> <span className="text-korinth-accent font-bold">{summary.activeRecs}</span></span>
      </div>

      <RegimeIndicator />

      {/* Sprint 20: Portfolio impact */}
      <PortfolioImpactPanel topic="Regime" {...impact} loading={loading} />

      <CrossAssetDashboard />

      {telegramMsgs.length > 0 && (
        <ModuleContainer title={<span className="flex items-center gap-2"><Send className="w-3 h-3 text-korinth-accent" /> Live Telegram Feed</span>} subtitle="High-importance signals">
          <div className="space-y-1.5">
            {telegramMsgs.map(m => (
              <div key={m.id} className="flex items-center gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                <span className="text-korinth-muted w-12">{m.sent_at?.slice(11, 16)}</span>
                <span className="font-bold text-korinth-text w-20 truncate">{m.display_name}</span>
                <span className="font-bold px-1 rounded" style={{ color: IMPORTANCE_COLORS[m.importance], backgroundColor: (IMPORTANCE_COLORS[m.importance] || '#6b7280') + '15' }}>{m.importance}</span>
                <span className="text-korinth-text flex-1 truncate">{m.summary || m.message_text?.slice(0, 100)}</span>
              </div>
            ))}
          </div>
          <a href="/telegram" className="block mt-2 text-[9px] font-mono text-korinth-accent hover:underline text-center">View full Telegram feed →</a>
        </ModuleContainer>
      )}

      <NewsFeed />

      <CostDashboard />

      {/* Sprint 20: Track record */}
      <TrackRecordPanel {...track} loading={loading} metric_name="Regime Classifier" />
    </div>
  );
}
